import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebaseConfig';
import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import useSound from 'use-sound';
import '@animxyz/core';
import { XyzTransition } from '@animxyz/react';
import { addTask } from '../../actions/taskActions';
import { Form, Button, Container, Col, Row } from 'react-bootstrap';
import task from '../../audio/task1.webm';

// 🎯 Ajoutez cette fonction pour convertir "2026-09-14T14:30" en "lundi 14 septembre 2026 à 14:30"
const formatFrenchDate = (dateIsoStr) => {
  if (!dateIsoStr || dateIsoStr.toLowerCase().trim() === 'aucun') return "Aucun rendez-vous";

  try {
    const dateObj = new Date(dateIsoStr);
    // Sécurité si la date est corrompue
    if (isNaN(dateObj.getTime())) return dateIsoStr;

    // Renvoie uniquement : "lundi 14 septembre 2026"
    return dateObj.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    return dateIsoStr;
  }
};

// Fonction pour mettre la première lettre en majuscule
const capitaliserMois = (locale, date) => {
  // Récupère le format natif ex: "septembre 2026"
  const labelIdoine = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
  // Retourne la chaîne avec la première lettre en majuscule
  return labelIdoine.charAt(0).toUpperCase() + labelIdoine.slice(1);
};

// 🎯 Attribue un titre et une couleur de badge selon le niveau de procrastination
const obtenirBadgeProcrastination = (count) => {
  if (!count || count === 0) {
    return {
      titre: "🏆 Maître Pro",
      couleur: "bg-success text-white",
      desc: "Zéro temps perdu, Bravo !"
    };
  } else if (count <= 2) {
    return {
      titre: "🏆 Maître Semi-Pro",
      couleur: "bg-success text-white",
      desc: "Excellent résultat !"
    };
  } else if (count > 2 && count <= 4) {
    return {
      titre: "⚖️ Flâneur Occasionnel",
      couleur: "bg-light text-dark",
      desc: "Attention à l'accumulation !"
    };
  } else {
    return {
      titre: "🦥 Légende de la Paresse",
      couleur: "bg-danger text-white",
      desc: "Le canapé a gagné ce mois-ci !"
    };
  }
};

function SimpleAddTask({ addTask }) {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [playSound] = useSound(task, { volume: 0.7 });

  const [startDate, setStartDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isPriorityDisabled, setIsPriorityDisabled] = useState(false);
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false);
  const [heure, setHeure] = useState(new Date().getHours());
  const [state, setState] = useState({
    task: '',
    checked: false,
    detail: '',
    priority: '',
    priorityColor: '',
    category: '',
    categoryColor: '',
    rdvDate: '',
    rdvTime: ''
  });

  const limite = 25;
  let message = "";

  if (heure >= 0 && heure < 6) {
    message = "Bonne nuit";
  } else if (heure >= 6 && heure < 12) {
    message = "Bonjour";
  } else if (heure >= 12 && heure < 18) {
    message = "Bon après-midi";
  } else {
    message = "Bonsoir";
  }

  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = db.collection('users').doc(currentUser.uid).onSnapshot(
      (doc) => {
        if (doc.exists) {
          setUserData(doc.data());
        }
      },
      (error) => {
        console.error("Erreur de chargement Firestore :", error);
      }
    );
    return unsubscribe;
  }, [currentUser]);

  useEffect(() => {
    const intervalle = setInterval(() => {
      setHeure(new Date().getHours());
    }, 60000);

    return () => clearInterval(intervalle);
  }, []);

  useEffect(() => {
    if (!currentUser || !userData) return;

    const verifierFinDeMois = async () => {
      const moisActuel = new Date().getMonth(); // 🟢 Ex: 8 pour Septembre, 9 pour Octobre...
      const dernierMoisVerifie = localStorage.getItem('memento_last_month');

      // 🎯 SI LE MOIS A CHANGÉ : On remet le compteur Firebase à 0 !
      if (dernierMoisVerifie !== null && parseInt(dernierMoisVerifie, 10) !== moisActuel) {
        try {
          await db.collection('users').doc(currentUser.uid).update({
            procrastinationCount: 0 // 🔄 Reset magique en base de données !
          });
          console.log("📅 Nouveau mois détecté ! Le compteur de procrastination a été remis à zéro.");
        } catch (err) {
          console.error("Erreur lors du reset de fin de mois :", err);
        }
      }

      // On mémorise le mois actuel pour le prochain contrôle
      localStorage.setItem('memento_last_month', moisActuel.toString());
    };

    verifierFinDeMois();
  }, [currentUser, userData]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    if (value.length <= limite) {
      setState({
        ...state,
        [id]: value
      });
    }
  };

  const addTaskForm = useRef(null);

  const setEnabledOptions = () => {
    setIsPriorityDisabled(false);
    setIsCategoryDisabled(false);
  };

  const handleClear = (e) => {
    setState({
      task: '',
      detail: '',
      priority: '',
      priorityColor: '',
      category: '',
      categoryColor: '',
      rdvDate: '',
      rdvTime: ''
    });
    setStartDate('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalTime = state.rdvDate && !state.rdvTime ? "Heure non spécifiée" : state.rdvTime;

    await addTask({
      ...state,
      detail: state.detail || 'Aucun',
      priority: state.priority || 'Aucune',
      priorityColor: state.priorityColor || '#56d21a',
      category: state.category || 'Aucune',
      categoryColor: state.categoryColor || '#56d21a',
      rdvDate: state.rdvDate || 'Aucun',
      rdvTime: finalTime || ''
    });

    playSound();
    if (addTaskForm.current) addTaskForm.current.reset();
    handleClear();
    setIsOpen(false);
  };

  const changeOptionsPriority = (e) => {
    const { name, value } = e.target;
    const idx = e.target.selectedIndex;
    const option = e.target.querySelectorAll('option')[idx];
    const priorityColor = option.getAttribute('data-color');

    // 🟢 ON BLOCKE L'OPTION PAR DÉFAUT ICI (Une fois le choix fait)
    setIsPriorityDisabled(true);

    setState((prev) => {
      return {
        ...prev,
        [name]: value,
        priorityColor: priorityColor
      };
    });
  };

  const changeOptionsCategory = (e) => {
    const { name, value } = e.target;
    const idx = e.target.selectedIndex;
    const option = e.target.querySelectorAll('option')[idx];
    const categoryColor = option.getAttribute('data-color');

    // 🟢 ON BLOCKE L'OPTION PAR DÉFAUT ICI (Une fois le choix fait)
    setIsCategoryDisabled(true);

    setState((prev) => {
      return {
        ...prev,
        [name]: value,
        categoryColor: categoryColor
      };
    });
  };

  const changeViewCalendar = (e) => {
    // L'input natif renvoie une chaîne au format "YYYY-MM-DDTHH:mm"
    const rawValue = e.target.value;

    if (!rawValue) {
      // Si le champ est vidé, on remet à zéro
      setState((prev) => ({ ...prev, rdvDate: '', rdvTime: '' }));
      setStartDate('');
      return;
    }

    // Séparation de la date et de l'heure natives
    const [, timePart] = rawValue.split('T');

    setState((prev) => {
      return {
        ...prev,
        rdvDate: rawValue,
        rdvTime: timePart || '12:00'
      };
    });
    setStartDate(rawValue); // Stocke la chaîne brute pour l'input
  };


  const handleClickViewCalendar = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
    setState((prev) => {
      return {
        ...prev,
        rdvDate: '',
        rdvTime: ''
      };
    });
    setStartDate('');
  };

  const firstName = userData?.firstName || "";

  // 🟢 On récupère le score de l'utilisateur stocké dans Firestore
  const scoreProcrastination = userData?.procrastinationCount || 0;
  const badge = obtenirBadgeProcrastination(scoreProcrastination);

  return (
    <React.Fragment>
      <Container className="d-flex flex-column align-items-center justify-content-center py-2 my-2">
        <h2 className="py-2 my-2 mb-1 text-center">{message} {firstName} !</h2>
        <span
          className={`badge ${badge.couleur} px-3 py-2 fs-6 mt-1 shadow-sm`}
          // style={{ borderRadius: '20px', cursor: 'help' }}
          style={{ fontWeight: '500', cursor: 'help' }}
          title={badge.desc}
        >
          {badge.titre} ({scoreProcrastination} {scoreProcrastination > 1 ? 'reports' : 'report'})
        </span>
      </Container>

      <Container className={`wrapper-container ${isOpen ? 'open' : ''}`}>
        <Form
          ref={addTaskForm}
          id='addTaskForm'
          autoComplete='off'
          onSubmit={handleSubmit}
          onClick={setEnabledOptions}
        >
          <Form.Group controlId='task'>
            <Form.Label>Saisie contrôlée ({state.task.length}/{limite})</Form.Label>
            <div className='input-group mb-2'>
              <Form.Control
                type='text'
                required
                maxLength={limite}
                value={state.task}
                onChange={handleChange}
                placeholder="Quelle action aujourd'hui ?"
              />
              <Button
                type='submit'
                variant='primary'
                className='input-group-text mx-0'
              >
                Ajouter
              </Button>
            </div>
          </Form.Group>
          <Form.Group controlId='detail'>
            <Form.Label>Saisie contrôlée ({state.detail.length}/{limite})</Form.Label>
            <div className='input-group'>
              <Form.Control
                type='text'
                maxLength={limite}
                value={state.detail}
                onChange={handleChange}
                placeholder="Ajouter un commentaire..."
              />
            </div>
          </Form.Group>
        </Form>

        <Row className="align-items-center g-1 w-100 mx-0">
          <Col xs={5} md={5}>
            <div className="input-group input-group-sm mt-3 p-2" style={{ overflow: 'visible' }}>
              <select
                name='priority'
                id="priority"
                onChange={changeOptionsPriority}
                value={state.priority}
                className='form-select px-1 px-sm-2 mobile-small-text'
                aria-label='Default select example'
              >
                <option value="" disabled={isPriorityDisabled} id='priority'>Priorité</option>
                <option data-color={'#56d21a'} value={'Aucune'}>
                  Aucune
              </option>
                <option data-color={'#f3e506'} value={'Importante'}>
                  Importante
              </option>
                <option data-color={'#ff0000'} value={'Urgente'}>
                  Urgente
              </option>
              </select>
            </div>
          </Col>

          <Col xs={2} md={2} className="text-center d-flex justify-content-center align-items-center">
            <XyzTransition
              appear
              xyz='fade small appear-stagger duration-20 delay-4 ease-out-back'
            >
              <i
                style={{ color: '#fff', fontSize: '2.3rem', cursor: 'pointer' }}
                className='d-flex align-items-end mx-3 material-icons'
                onClick={handleClickViewCalendar}
                title={isOpen ? "Masquer les options" : "Planifier un rendez-vous"}
              >
                calendar_month
            </i>
            </XyzTransition>
          </Col>

          <Col xs={5} md={5}>
            <div className="input-group input-group-sm mt-3 p-2" style={{ overflow: 'visible' }}>
              <select
                name='category'
                id="category"
                onChange={changeOptionsCategory}
                value={state.category}
                className='form-select px-1 px-sm-2 mobile-small-text'
                aria-label='Default select example'
              >
                <option value="" disabled={isCategoryDisabled} id='category'>Catégorie</option>
                <option data-color={'#56d21a'} value={'Aucune'}>
                  Aucune
                </option>
                <option data-color={'#53cabc'} value={'Achat'}>
                  Achat
                </option>
                <option data-color={'#f057e2'} value={'Administratif'}>
                  Administratif
                </option>
                <option data-color={'#f2b0ef'} value={'Alimentation'}>
                  Alimentation
                </option>
                <option data-color={'#f20202'} value={'Anniversaire'}>
                  Anniversaire
                </option>
                <option data-color={'#abcdef'} value={'Automobile'}>
                  Automobile
                </option>
                <option data-color={'#dce5e4'} value={'Banque'}>
                  Banque
                </option>
                <option data-color={'#b78af4'} value={'Bien-être'}>
                  Bien-être
                </option>
                <option data-color={'#f1573d'} value={'Bricolage'}>
                  Bricolage
                </option>
                <option data-color={'#a9f5bc'} value={'Budget'}>
                  Budget
                </option>
                <option data-color={'#459AB1'} value={'Courses'}>
                  Courses
                </option>
                <option data-color={'#9898e1'} value={'Facture'}>
                  Facture
                </option>
                <option data-color={'#0fadc2'} value={'Famille'}>
                  Famille
                </option>
                <option data-color={'#b1ff1e'} value={'Jardinage'}>
                  Jardinage
                </option>
                <option data-color={'#f4c16e'} value={'Loisirs'}>
                  Loisirs
                </option>
                <option data-color={'#f78181'} value={'Maison'}>
                  Maison
                </option>
                <option data-color={'#1fd15d'} value={'Médical'}>
                  Médical
                </option>
                <option data-color={'#fe2e64'} value={'Personnel'}>
                  Personnel
                </option>
                <option data-color={'#ffffff'} value={'Professionnel'}>
                  Professionnel
                </option>
                <option data-color={'#269255'} value={'Scolaire'}>
                  Scolaire
                </option>
                <option data-color={'#0ff5e7'} value={'Sport'}>
                  Sport
                </option>
                <option data-color={'#ff8000'} value={'Travail'}>
                  Travail
              </option>
              </select>
            </div>
          </Col>
        </Row>

        <div className='d-flex mt-1'>
          <div className="extra-content position-relative">
            <Form.Group controlId="rdvPicker" className="px-1">
              <Row className="mx-0">
                <Form.Label className="text-light mb-2">Date et Heure du rendez-vous</Form.Label>
                {/* <div className="d-flex flex-row align-items-start gap-4"> */}
                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-1 gap-sm-3 w-100 px-0">
                  <div className="flex-grow-1">
                    <Form.Control
                      type="text"
                      className="form-control text-white bg-profil border-secondary mb-2"
                      placeholder="📅 Cliquez pour choisir un jour..."
                      value={state.rdvDate && state.rdvDate !== 'Aucun' ? formatFrenchDate(state.rdvDate) : ''}
                      onFocus={() => setShowCalendar(true)}
                      readOnly // Empêche d'écrire n'importe quoi, force le clic
                      style={{ cursor: 'pointer' }}
                    />
                  </div>

                  {state.rdvDate && state.rdvDate !== 'Aucun' && (
                    <div className="animate-fade-in">
                      <Form.Control
                        type="time"
                        step="300"
                        className="form-control text-white bg-profil text-center border-secondary mb-2"
                        style={{ width: '100%', minWidth: '100px', maxWidth: '100%' }}
                        value={state.rdvTime || "12:00"}
                        onChange={(e) => {
                          const dateNettoyee = state.rdvDate ? state.rdvDate.split('T')[0] : moment().format('YYYY-MM-DD');
                          const nouvelleHeure = e.target.value.slice(0, 5); // Conserve uniquement HH:mm
                          const syntheticEvent = {
                            target: { value: `${dateNettoyee}T${nouvelleHeure}` }
                          };
                          changeViewCalendar(syntheticEvent);
                        }}
                      />
                    </div>
                  )}
                </div>
              </Row>
            </Form.Group>

            {/* 🎯 VOTRE CALENDRIER EN POP-UP APPARAISSANT AU CLIC */}
            {showCalendar && (
              <div
                className="position-absolute shadow-lg p-2 rounded border border-secondary bg-profil"
                style={{
                  top: '100%',
                  left: 0,
                  width: '320px',
                  marginTop: '15px',
                  maxWidth: '350px',
                  zIndex: 99999
                }}
              >
                <Calendar
                  locale="fr-FR"
                  formatMonthYear={capitaliserMois}
                  value={startDate && startDate !== 'Aucun' ? new Date(startDate) : new Date()}
                  formatShortWeekday={(locale, date) =>
                    date.toLocaleDateString(locale, { weekday: 'short' }).toUpperCase()
                  }
                  onChange={(selectedDate) => {
                    const dateUniquement = moment(selectedDate).format('YYYY-MM-DD');
                    const currentTime = state.rdvTime && state.rdvTime.trim() !== "" ? state.rdvTime.slice(0, 5) : "12:00";

                    setStartDate(selectedDate);

                    const syntheticEvent = {
                      target: { value: `${dateUniquement}T${currentTime}` }
                    };

                    changeViewCalendar(syntheticEvent);
                    setShowCalendar(false); // Referme le calendrier après la sélection
                  }}
                />
                <button
                  className="btn btn-sm btn-primary w-100 mt-2 text-white"
                  onClick={() => setShowCalendar(false)}
                >
                  Fermer
                </button>
              </div>
            )}

            {/* Rendu de votre confirmation existante */}
            {state.rdvDate && state.rdvDate !== 'Aucun' && (
              <React.Fragment>
                <div className="text-light text-select fw-semibold">Date sélectionnée:</div>
                <div className="text-center text-warning fw-semibold">{formatFrenchDate(state.rdvDate)} à {state.rdvTime}</div>
              </React.Fragment>
            )}
          </div>
        </div>
      </Container>
    </React.Fragment>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    addTask: (task) => dispatch(addTask(task))
  };
};

export default connect(null, mapDispatchToProps)(SimpleAddTask);
